import { useCallback, useState } from "react"
import styled from "styled-components"

import { UITypes } from "types"

import { getDateRangeFromOption } from "utils/dateUtils"
import { DateRangePickerSelect } from "components/common/DateRangePickerSelect"

import { TrendColumn, type TrendColumnData } from "./TrendColumn"

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 16px 24px;
  flex: 1 0 0;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media only screen and (max-width: 768px) {
    flex: 1 0 100%;
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  width: 100%;
`

const CardTitle = styled.h3`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.176px;
  margin: 0;
  color: #0f172a;
  white-space: pre-wrap;
`

const Divider = styled.div`
  border-top: 1px solid #f1f5f9;
  width: 100%;
  flex-shrink: 0;
`

const ColumnsContainer = styled.div`
  display: flex;
  gap: 16px;
  height: 80px;
  width: 100%;
`

const defaultTimeRangeOptions: UITypes.Option[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
]

export const DEFAULT_PERIOD = 7

const parsePeriodFromOption = (value: string | number | boolean): number => {
  const str = String(value)
  const match = str.match(/(\d+)/)
  return match ? Number(match[1]) : DEFAULT_PERIOD
}

export interface TrendCardProps {
  title: string
  columns: TrendColumnData[]
  onPeriodChange?: (period: number) => void
}

export const TrendCard = ({ title, columns, onPeriodChange }: TrendCardProps) => {
  const [, setDateRange] = useState(() => getDateRangeFromOption(String(defaultTimeRangeOptions[0].value)))

  const handleSelectDateRange = useCallback((newDateRange: ReturnType<typeof getDateRangeFromOption>) => {
    setDateRange(newDateRange)
  }, [])

  const handleOptionChange = useCallback(
    (option: UITypes.Option) => {
      onPeriodChange?.(parsePeriodFromOption(option.value))
    },
    [onPeriodChange],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <DateRangePickerSelect
          options={defaultTimeRangeOptions}
          defaultOption={defaultTimeRangeOptions[0]}
          onSelectDateRange={handleSelectDateRange}
          onOptionChange={handleOptionChange}
        />
      </CardHeader>
      <Divider />
      <ColumnsContainer>
        {columns.map((col, i) => (
          <TrendColumn key={col.label} {...col} hasBorder={i < columns.length - 1} />
        ))}
      </ColumnsContainer>
    </Card>
  )
}
